import React from "react";
import { Button, CircularProgress, Alert, Snackbar } from "@mui/material";
import { SaveOutlined } from "@mui/icons-material";
import { useSaveAssemblyMutation } from "../api/playbookApi";
import { getCurrentDocumentAsBlob } from "../../../utils/taskpane";
import { AssemblyApiContext } from "../types/assembly-context";

interface SaveAssemblyProps {
  ctx: AssemblyApiContext | null;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  variant?: "contained" | "outlined" | "text";
}

const SaveAssemblyButton = ({
  ctx,
  disabled = false,
  size = "small",
  variant = "contained",
}: SaveAssemblyProps) => {
  const [saveAssembly, { isLoading: isSaving, isError, isSuccess, reset }] =
    useSaveAssemblyMutation();

  const handleSaveAssembly = async () => {
    if (!ctx) return;

    const documentBlob = await getCurrentDocumentAsBlob();
    const documentFile = new File([documentBlob], "current-document.docx", {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAssembly({ ctx, docxFile: documentFile });
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={isSaving || !ctx || disabled}
        onClick={handleSaveAssembly}
        startIcon={isSaving ? <CircularProgress size={16} /> : <SaveOutlined fontSize="small" />}
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>

      <Snackbar
        open={isSuccess}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => reset()}
      >
        <Alert severity="success" variant="filled" sx={{ minWidth: "240px" }}>
          Saved successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={isError}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => reset()}
      >
        <Alert severity="error" variant="filled" sx={{ minWidth: "240px" }}>
          Failed to save document. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveAssemblyButton;
